/*
 * Copyright (c) 2022 Honeywell International, Inc. All rights reserved.
 *
 * This file contains trade secrets of Honeywell International, Inc.  No part
 *  may be reproduced or transmitted in any form by any means or for any
 *  purpose without the express written permission of Honeywell.
 *
 */


import { action, computed, observable } from 'mobx';
import {
  Admin_Portal_LaunchDarklyEnvKeyName,
  launchDarklyFeatureToggleKey
} from '../../launchDarklyConfig';
import { LDClient, initialize } from 'launchdarkly-js-client-sdk';
import FeatureFlagStore from './FeatureFlagsStore';
// NOTE:
// - flags are mobx observable either from the flags or your own computed
// property for convenience
// - you can use flags in stores or other non-React.Component locations
// (functions/classes) but the value can not be "reactive" outside a Component.

export interface Context { status: boolean };

/**
 * FeatureFlagStore for Admin Portal LaunchDarkly
 */
export class APFeatureFlagsStore extends FeatureFlagStore {

  @observable
  public userContextInitialized: Context = { status: false};

  getLaunchDarklyEnvKey(){
    return this.envVariables[Admin_Portal_LaunchDarklyEnvKeyName];
  }

  @action
  setFeatureFlags = (client: LDClient) => {
    this.featureFlags['forgeDiyAppEnabled'] = client.variation(launchDarklyFeatureToggleKey.ADMIN_PORTAL.DIY_FORGE_APP, true);
    this.featureFlags['gwRegEnabled'] = client.variation(launchDarklyFeatureToggleKey.ADMIN_PORTAL.DIY_GW_REG_FEATURE_KEY, true);
    this.featureFlags['virtualGatewayEnabled'] = client.variation(launchDarklyFeatureToggleKey.ADMIN_PORTAL.VIRTUAL_GATEWAY_ENABLED, false);
    this.featureFlags['simplyConnectEnabled'] = client.variation(launchDarklyFeatureToggleKey.ADMIN_PORTAL.SIMPLY_CONNECT_ENABLED, true);
    this.featureFlags['forgeEPMEnabled'] = client.variation(launchDarklyFeatureToggleKey.ADMIN_PORTAL.FORGE_EPM_ENABLED, false);
    this.featureFlags['fc-gateway-azure-provisioning'] = client.variation(launchDarklyFeatureToggleKey.ADMIN_PORTAL.GATEWAY_AZURE_PROVISIONING, false);
    this.featureFlags['fc-gateway-azure-provisioning-site'] = client.variation(launchDarklyFeatureToggleKey.ADMIN_PORTAL.GATEWAY_AZURE_PROVISIONING_SITE, false);
  };

  /**
 * Change the user associated with the launch darkly client
 */
  identify(userPrincipalName: string) {
    let ldUser = {
      key: userPrincipalName,
      custom: {}
    };
    this.client?.identify(ldUser, "", () => {
      this.userContextInitialized.status = true;
    });
    //this.client.identify(ldUser);
  }

  /**
   * Update the Feature Flags based on siteId (Site level flag)
   * @param siteId 
   */
   updateLaunchDarklyFlag = async (siteId: string) =>
   {
     let user = this.getLDUserInfo();
     user.custom = { SiteId: siteId };
 
     let client = initialize(this.getLaunchDarklyEnvKey(), user);
     await client.waitForInitialization();
    this.setFeatureFlags(client);
   }

  @computed
  get getGatewayRegistrationEnabled() {
    return this.featureFlags['gwRegEnabled'];
  }

  @computed
  get getForgeDiyAppEnabled() {
    return this.featureFlags['forgeDiyAppEnabled'];
  }

  @computed
  get getVirtualGatewayEnabled() {
    return this.featureFlags['virtualGatewayEnabled'];
  }

  @computed
  get getSimplyConnectEnabled() {
    return this.featureFlags['simplyConnectEnabled'];
  }

  @computed
  get getForgeEPMEnabled(){
    return this.featureFlags['forgeEPMEnabled'];
  }

  @computed
  get getGatewayAzureProvisioningEnabled(){
    return this.featureFlags['fc-gateway-azure-provisioning'];
  }

  @computed
  get getGatewayAzureProvisioningForSite(){
    return this.featureFlags['fc-gateway-azure-provisioning-site'];
  }

}

export default new APFeatureFlagsStore();
